import React, { useState } from 'react';
import { DnDTable, ColumnData } from './DnDTable';

interface Person {
  id: number;
  name: string;
  email: string;
  role: string;
  department: string;
  status: 'active' | 'inactive';
}

const sampleData: Person[] = [
  {
    id: 1,
    name: 'John Doe',
    email: 'john.doe@example.com',
    role: 'Developer',
    department: 'Engineering',
    status: 'active',
  },
  {
    id: 2,
    name: 'Jane Smith',
    email: 'jane.smith@example.com',
    role: 'Designer',
    department: 'Design',
    status: 'active',
  },
  {
    id: 3,
    name: 'Robert Johnson',
    email: 'robert.johnson@example.com',
    role: 'Product Manager',
    department: 'Product',
    status: 'active',
  },
  {
    id: 4,
    name: 'Emily Williams',
    email: 'emily.williams@example.com',
    role: 'QA Engineer',
    department: 'Quality Assurance',
    status: 'inactive',
  },
  {
    id: 5,
    name: 'Michael Brown',
    email: 'michael.brown@example.com',
    role: 'Team Lead',
    department: 'Engineering',
    status: 'active',
  },
  {
    id: 6,
    name: 'Sarah Davis',
    email: 'sarah.davis@example.com',
    role: 'UX Researcher',
    department: 'Design',
    status: 'active',
  },
  {
    id: 7,
    name: 'David Miller',
    email: 'david.miller@example.com',
    role: 'DevOps Engineer',
    department: 'Operations',
    status: 'inactive',
  },
];

// Sample data with subrows
const sampleDataWithSubrows = sampleData.map((person) => ({
  ...person,
  subRows:
    person.id % 2 === 0
      ? [
          {
            id: 100 + person.id,
            name: `Assistant to ${person.name}`,
            email: `assistant.${person.name.toLowerCase().replace(' ', '.')}@example.com`,
            role: 'Assistant',
            department: person.department,
            status: 'active',
          },
        ]
      : [],
}));

export const DnDTableExample: React.FC = () => {
  const [people, setPeople] = useState<Person[]>(sampleData);
  const [peopleWithSubrows, setPeopleWithSubrows] = useState(sampleDataWithSubrows);
  const [selection, setSelection] = useState<Person[]>([]);

  const columns: ColumnData<Person>[] = [
    {
      id: 'name',
      field: 'name',
      header: 'Name',
      sort: true,
    },
    {
      id: 'email',
      field: 'email',
      header: 'Email',
      sort: true,
    },
    {
      id: 'role',
      field: 'role',
      header: 'Role',
      sort: true,
    },
    {
      id: 'department',
      field: 'department',
      header: 'Department',
      sort: true,
    },
    {
      id: 'status',
      field: 'status',
      header: 'Status',
      sort: true,
      renderer: (data) => (
        <span
          style={{
            color: data.status === 'active' ? 'green' : 'red',
            fontWeight: 'bold',
          }}>
          {data.status.toUpperCase()}
        </span>
      ),
    },
  ];

  const handleReorder = (reorderedData: Person[]) => {
    console.log('Reordered data:', reorderedData);
    setPeople(reorderedData);
  };

  const handleSelectionChange = (selectedData: Person[]) => {
    console.log('Selected data:', selectedData);
    setSelection(selectedData);
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2>Basic DnDTable Example</h2>
      <p>Drag and drop rows to reorder them</p>

      <DnDTable
        data={people}
        columns={columns}
        rowSelection={true}
        onSelectionChange={handleSelectionChange}
        noDataText="No people found"
        variant="filled"
        draggable={true}
        onReorder={handleReorder}
      />

      <h2 style={{ marginTop: '40px' }}>DnDTable with Expandable Rows</h2>
      <p>Some rows have subrows that can be expanded</p>

      <DnDTable
        data={peopleWithSubrows}
        columns={columns}
        rowSelection={true}
        onSelectionChange={handleSelectionChange}
        noDataText="No people found"
        variant="filled"
        draggable={true}
        expandable={true}
      />
    </div>
  );
};
